import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Row, Col, Card, CardBody, Table } from "reactstrap"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import TableShimmer from "components/Shared/TableShimmer"
import Breadcrumbs from "components/Common/Breadcrumb"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import TableActionsDropdown from "components/Shared/TableActionsDropdown"
import Paginations from "components/Common/Paginations"

import {
  getAllMenuSetup,
  updateMenu,
  deleteMenu,
} from "store/actions/menuSetupAction"
import DeleteModal from "components/Common/DeleteModal"
import Switch from "components/Shared/Switch"
import { showAlert } from "store/reducers/alertReducer"
import formatDateTime from "components/Shared/formatDateTime"
import ConfirmModal from "components/Common/ConfirmModal"
import { getSubPermissions } from "common/isPermissionEnabled"

const MenuSetup = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { userRole, accountType } = useSelector(state => state.Login)

  const canViewPosMenuFields = useMemo(() => {
    const addSubs = getSubPermissions(userRole, 4, "menus_2", accountType) || {}
    const updateSubs =
      getSubPermissions(userRole, 4, "menus_3", accountType) || {}
    return (
      addSubs.link_menu_with_pos?.isEnabled === true ||
      updateSubs.link_menu_with_pos?.isEnabled === true
    )
  }, [userRole, accountType])
  const [selectedId, setSelectedId] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [itemIdToDelete, setItemIdToDelete] = useState(null)
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [selectedMenuItem, setSelectedMenuItem] = useState(null)

  const fetchMenuSetup = async (page = 1) => {
    dispatch(
      getAllMenuSetup({
        page: page,
        limit: itemsPerPage || 10,
      })
    )
  }

  // Pagination handler
  const onPageChange = page => {
    fetchMenuSetup(page)
  }

  useEffect(() => {
    fetchMenuSetup(1)
  }, [])

  const {
    loading,
    isLoading,
    totalCount,
    totalPages,
    currentPage,
    itemsPerPage,
    menus,
  } = useSelector(state => state.MenuSetup)

  const handleStatusChange = async item => {
    try {
      const payload = {
        id: item._id,
        status: item.status === 1 ? 0 : item.status === 0 ? 1 : item.status,
      }
      const response = await dispatch(updateMenu(payload))
      if (response.payload.statusCode === 200) {
        dispatch(
          showAlert({ text: t("Status updated successfully"), type: "success" })
        )
        fetchMenuSetup(currentPage || 1)
      }
    } catch (error) {
      console.error(error)
    }
  }

  const handleDelete = () => {
    const payload = {
      id: itemIdToDelete,
    }
    dispatch(deleteMenu(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload || {}
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          setShowDeleteModal(false)
          setItemIdToDelete(null)
          fetchMenuSetup(currentPage || 1)
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(() => {})
  }

  const confirmStatusUpdate = async () => {
    if (!selectedMenuItem) return
    try {
      const payload = {
        id: selectedMenuItem._id,
        status:
          selectedMenuItem.status === 1
            ? 0
            : selectedMenuItem.status === 0
            ? 1
            : selectedMenuItem.status,
      }
      const response = await dispatch(updateMenu(payload))
      if (response.payload.statusCode === 200) {
        dispatch(
          showAlert({ text: t("Status updated successfully"), type: "success" })
        )
        fetchMenuSetup(currentPage || 1)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setSelectedMenuItem(null)
      setShowStatusModal(false)
    }
  }

  const {
    i18n: { language },
  } = useTranslation()

  const renderCountryNames = countriesList => {
    if (
      !countriesList ||
      (Array.isArray(countriesList) && countriesList.length === 0)
    ) {
      return "-"
    }

    const list = Array.isArray(countriesList) ? countriesList : [countriesList]

    const names = list
      .map(country => {
        if (!country) return null
        if (typeof country === "string") return country
        return language === "sa"
          ? country.nameAr || country.name || country.countryCode
          : country.name || country.nameAr || country.countryCode
      })
      .filter(Boolean)

    return names.length ? names.join(", ") : "-"
  }

  const getMenuActionItems = item => [
    {
      key: "edit",
      content: (
        <>
          <i className="mdi mdi-pencil font-size-16 text-success me-1"></i>
          {t("Edit")}
        </>
      ),
      onClick: () => navigate(`/menu/menu-setup/edit?id=${item?._id}`),
    },
    {
      key: "view",
      content: (
        <>
          <i className="mdi mdi-eye font-size-16 text-primary me-1"></i>
          {t("View")}
        </>
      ),
      onClick: () => navigate(`/menu/menu-setup/view?id=${item?._id}`),
    },
    {
      key: "delete",
      content: (
        <>
          <i className="mdi mdi-trash-can font-size-16 text-danger me-1"></i>
          {t("Delete")}
        </>
      ),
      onClick: () => {
        setItemIdToDelete(item?._id)
        setShowDeleteModal(true)
      },
    },
  ]

  return (
    <div className="page-content ">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <DataExportDropdown
                cardTitle={t("Menu")}
                isAddBtn={true}
                addBtnText={t("Add Setup")}
                onAddBtnClick={() => navigate("/menus/menu-setup/add")}
              />

              <CardBody>
                <div className="table-responsive">
                  {loading ? (
                    <TableShimmer row={7} col={canViewPosMenuFields ? 5 : 3} />
                  ) : menus.length === 0 ? (
                    <p className="text-center my-3">{t("No data found")}</p>
                  ) : (
                    <Table className="table mb-0" hover>
                      <thead className="table-light full_th">
                        <tr>
                          <th>#</th>
                          {canViewPosMenuFields && <th>{t("POS ID")}</th>}
                          {canViewPosMenuFields && <th>{t("App Menu ID")}</th>}
                          <th>{t("Menu Name (EN)")}</th>
                          <th>{t("Menu Name (AR)")}</th>
                          <th>{t("Brand")}</th>
                          <th>{t("Country")}</th>
                          <th>{t("Status")}</th>
                          <th>{t("Created By")}</th>
                          <th>{t("Created At")}</th>
                          <th>{t("Actions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {menus?.map((item, index) => (
                          <tr key={item._id}>
                            <td>
                              {(currentPage - 1) * itemsPerPage + index + 1}
                            </td>
                            {canViewPosMenuFields && (
                              <td>{item?.menuId || "-"}</td>
                            )}
                            {canViewPosMenuFields && (
                              <td>{item?.appMenuId || "-"}</td>
                            )}
                            <td>{item?.menuNameEn || "-"}</td>
                            <td>{item?.menuNameAr || "-"}</td>

                            <td>{item?.brand?.brandName ?? "-"}</td>
                            <td>{renderCountryNames(item?.countries)}</td>
                            <td>
                              <Switch
                                onClickHandler={e => {
                                  e.stopPropagation()
                                  setSelectedMenuItem(item)
                                  setShowStatusModal(true)
                                }}
                                isChecked={item.status}
                                disabled={item._id === selectedId && isLoading}
                              />
                            </td>
                            <td>
                              {item?.createdBy
                                ? `${item.createdBy.firstName || ""} ${item.createdBy.lastName || ""}`.trim() ||
                                  item.createdBy.email ||
                                  "-"
                                : "-"}
                            </td>
                            <td>{formatDateTime(item?.createdAt) || "-"}</td>
                            <td>
                              <TableActionsDropdown
                                items={getMenuActionItems(item)}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </div>
                {!loading && menus?.length > 0 && (
                  <div className="mt-3">
                    <Paginations
                      data={menus}
                      itemsPerPage={itemsPerPage}
                      onPageChange={onPageChange}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCount={totalCount}
                    />
                  </div>
                )}
              </CardBody>
              <ConfirmModal
                show={showStatusModal}
                onDeleteClick={confirmStatusUpdate}
                isLoading={isLoading}
                onCloseClick={() => {
                  setSelectedMenuItem(null)
                  setShowStatusModal(false)
                }}
                buttonText={t("Yes")}
                bodyTitle={t("Are you sure you want to update the status?")}
              />
              <DeleteModal
                show={showDeleteModal}
                onDeleteClick={handleDelete}
                isLoading={isLoading}
                onCloseClick={() => {
                  setItemIdToDelete(null)
                  setShowDeleteModal(false)
                }}
                buttonText={t("Delete")}
                bodyTitle={t("Are you sure you want to delete this {{name}}?", {
                  name: t("Menu"),
                })}
              />
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}
export default MenuSetup
